import React, { useState, useRef } from 'react';
import { Upload, X, File, Image, Trash2, Eye, Cloud, Download } from 'lucide-react';
import surveyService from '../services/surveyService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const FileUpload = ({ surveyId, environmentId = null, existingFiles = [], onFilesChange, disabled = false }) => {
  const [files, setFiles] = useState(existingFiles);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    
    // Validar arquivos
    const validFiles = [];
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    fileArray.forEach(file => {
      if (file.size > maxSize) {
        toast.error(`Arquivo ${file.name} é muito grande (máximo 50MB)`);
        return;
      }
      
      if (!allowedTypes.includes(file.mimetype || file.type)) {
        toast.error(`Tipo de arquivo ${file.name} não permitido`);
        return;
      }
      
      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  };

  const uploadFiles = async (filesToUpload) => {
    if (!surveyId) {
      toast.error('Salve o levantamento primeiro antes de fazer upload de arquivos');
      return;
    }

    try {
      setUploading(true);
      console.log('📤 Fazendo upload de', filesToUpload.length, 'arquivos...');

      const metadata = {
        file_type: 'PHOTO',
        category: 'levantamento',
        description: environmentId ? `Arquivo do ambiente ${environmentId}` : 'Arquivo do levantamento',
        environment_id: environmentId
      };

      const response = await surveyService.uploadFiles(surveyId, filesToUpload, metadata);
      console.log('✅ Upload concluído:', response);

      const newFiles = [...files, ...response.data];
      setFiles(newFiles);
      onFilesChange && onFilesChange(newFiles);
      
      toast.success(`${filesToUpload.length} arquivo(s) enviado(s) com sucesso!`);
    } catch (error) {
      console.error('❌ Erro no upload:', error);
      toast.error('Erro no upload: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    setConfirmAction(() => async () => {
      try {
        console.log('🗑️ Deletando arquivo:', fileId);
        await surveyService.deleteFile(fileId);

        const updatedFiles = files.filter(f => f.id !== fileId);
        setFiles(updatedFiles);
        onFilesChange && onFilesChange(updatedFiles);

        toast.success('Arquivo deletado com sucesso');
      } catch (error) {
        console.error('❌ Erro ao deletar arquivo:', error);
        toast.error('Erro ao deletar arquivo: ' + (error.response?.data?.message || error.message));
      }
    });
    setShowConfirmModal(true);
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleDownloadFile = async (file) => {
    try {
      let downloadUrl = file.file_path;

      // Se for Cloudinary, usar a URL direta
      if (file.is_cloudinary_file && file.cloudinary_url) {
        downloadUrl = file.cloudinary_url;
      }

      // Criar link de download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.original_name || 'arquivo';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('❌ Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const openFileDialog = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) {
      return <Image className="w-6 h-6 text-blue-500" />;
    }
    return <File className="w-6 h-6 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {/* Área de Upload */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver 
            ? 'border-blue-500 bg-blue-50' 
            : disabled 
              ? 'border-gray-200 bg-gray-50' 
              : 'border-gray-300 hover:border-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        {uploading ? (
          <div className="space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-blue-600">Enviando arquivos...</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Upload className={`w-12 h-12 mx-auto ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
            <div>
              <p className={`text-lg font-medium ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                📱 Upload de Arquivos do Tablet
              </p>
              <p className={`text-sm ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>
                📸 Fotos, PDFs, documentos do levantamento
              </p>
            </div>
            
            {!disabled && (
              <>
                <button
                  type="button"
                  onClick={openFileDialog}
                  className="bg-blue-600 text-white px-4 sm:px-6 py-3 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Selecionar Arquivos
                </button>
                <p className="text-xs text-gray-500">
                  Ou arraste e solte arquivos aqui<br/>
                  Máximo: 50MB por arquivo | Tipos: JPG, PNG, PDF, DOC, TXT
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Lista de Arquivos */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900">
            📁 Arquivos Enviados ({files.length})
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {files.map((file) => (
              <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                {file.is_cloudinary_file ? (
                  <Cloud className="w-6 h-6 text-blue-500" />
                ) : file.is_drive_file ? (
                  <Cloud className="w-6 h-6 text-green-500" />
                ) : (
                  getFileIcon(file.mime_type)
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.original_name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatFileSize(file.file_size)}</span>
                    <span>•</span>
                    <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {file.file_type}
                    </span>
                    {file.is_cloudinary_file && (
                      <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                        <Cloud className="w-3 h-3" /> Cloudinary
                      </span>
                    )}
                    {file.is_drive_file && (
                      <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
                        <Cloud className="w-3 h-3" /> Google Drive
                      </span>
                    )}
                  </div>
                  {file.description && (
                    <p className="text-xs text-gray-600 mt-1">{file.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {file.is_cloudinary_file && file.cloudinary_url ? (
                    <button
                      type="button"
                      className="p-2 text-blue-600 hover:text-blue-800 rounded"
                      title="Abrir no Cloudinary"
                      onClick={() => window.open(file.cloudinary_url, '_blank')}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  ) : file.is_drive_file && file.drive_web_view_link ? (
                    <button
                      type="button"
                      className="p-2 text-green-600 hover:text-green-800 rounded"
                      title="Abrir no Google Drive"
                      onClick={() => window.open(file.drive_web_view_link, '_blank')}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  ) : file.mime_type?.startsWith('image/') && !file.is_cloudinary_file && !file.is_drive_file ? (
                    <button
                      type="button"
                      className="p-2 text-blue-600 hover:text-blue-800 rounded"
                      title="Visualizar"
                      onClick={() => {
                        // TODO: Implementar preview de imagem
                        toast.info('Preview de imagem será implementado em breve');
                      }}
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  ) : null}

                  {file.is_cloudinary_file && file.cloudinary_url && (
                    <button
                      type="button"
                      className="p-2 text-blue-600 hover:text-blue-800 rounded"
                      title="Baixar"
                      onClick={() => handleDownloadFile(file)}
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  )}

                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleDeleteFile(file.id)}
                      className="p-2 text-red-600 hover:text-red-800 rounded"
                      title="Deletar"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instruções */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">💡 Dicas para Upload:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>📱 Arquivos do Tablet:</strong> Plantas, medidas, anotações digitais</li>
          <li>• <strong>📸 Fotos Importantes:</strong> Ambiente, limitações, detalhes técnicos</li>
          <li>• <strong>📄 Documentos:</strong> Projetos existentes, especificações</li>
          <li>• <strong>⚡ Múltiplos arquivos:</strong> Selecione vários de uma vez</li>
          <li>• <strong>☁️ Google Drive:</strong> Arquivos são salvos na nuvem (não ocupam espaço no servidor)</li>
        </ul>
      </div>

      <ConfirmModal
        show={showConfirmModal}
        onConfirm={() => {
          setShowConfirmModal(false);
          confirmAction && confirmAction();
          setConfirmAction(null);
        }}
        onCancel={handleConfirmModalClose}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja deletar este arquivo?"
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default FileUpload;
